import type { ScanRules, SnapshotRecord } from '@jalsa/shared';

import type { Api } from '../api';
import type { LocalProvider } from '../check-session';
import type { CheckMode } from '../types';
import { LocalCheck } from './local-check';
import { SnapshotStore } from './store';

/**
 * Hält den Offline-Abzug aktuell und stellt die lokale Prüfung bereit.
 *
 * Ablauf: beim Start den gespeicherten Abzug laden (sofort einsatzbereit,
 * auch ohne Netz), danach im Hintergrund Änderungen nachziehen. Verlangt
 * der Server einen Vollabzug. Etwa weil der Stand zu alt ist. Wird er
 * geholt.
 */
export class SnapshotSync implements LocalProvider {
  private engine: LocalCheck | null = null;
  private version: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly api: Api,
    private readonly store: SnapshotStore = new SnapshotStore(),
    private readonly intervalMs = 60_000,
  ) {}

  /** Der einzige Weg, wie CheckSession an die lokale Prüfung kommt. */
  check(mode: CheckMode, query?: string, personId?: string) {
    return this.engine ? this.engine.check(mode, query, personId) : null;
  }

  get ready(): boolean {
    return this.engine !== null;
  }

  get count(): number {
    return this.records.length;
  }

  private records: SnapshotRecord[] = [];

  async start(): Promise<void> {
    await this.loadFromStore();
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), this.intervalMs);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private async loadFromStore(): Promise<void> {
    const stored = await this.store.load();
    if (!stored) return;
    this.apply(stored.area, stored.version, stored.records, stored.scan);
  }

  /** Änderungen nachziehen; ohne brauchbaren Stand einen Vollabzug holen. */
  async refresh(): Promise<void> {
    if (this.version) {
      const delta = await this.api.snapshotDelta(this.version);
      if (!delta.ok) return;

      if (!delta.data.fullRefreshRequired) {
        const merged = mergeRecords(this.records, delta.data.records, delta.data.removedIds);
        // Das Delta trägt keine Regel. Die zuletzt bekannte bleibt gültig.
        this.apply(delta.data.area, delta.data.version, merged, this.scan);
        await this.persist(delta.data.area, delta.data.version, merged);
        return;
      }
    }

    const full = await this.api.snapshot();
    if (!full.ok) return;
    this.apply(full.data.area, full.data.version, full.data.records, full.data.scan);
    await this.store.save(full.data);
  }

  /** Bei Gerätesperre oder Abmeldung: nichts Entschlüsselbares zurücklassen. */
  async wipe(): Promise<void> {
    this.stop();
    this.engine = null;
    this.records = [];
    this.version = null;
    this.scan = undefined;
    await this.store.wipe();
  }

  private scan: ScanRules | undefined;

  private apply(
    area: string,
    version: string,
    records: SnapshotRecord[],
    scan?: ScanRules,
  ): void {
    this.version = version;
    this.records = records;
    if (scan) this.scan = scan;
    this.engine = new LocalCheck(records, area, {
      ...(this.scan?.prefixes ? { scanPrefixes: this.scan.prefixes } : {}),
      ...(this.scan?.patterns ? { scanPatterns: this.scan.patterns } : {}),
    });
  }

  private async persist(area: string, version: string, records: SnapshotRecord[]): Promise<void> {
    const stored = await this.store.load();
    await this.store.save({
      area,
      version,
      generatedAt: version,
      // Jede Aktualisierung verlängert die Haltbarkeit. Ein Gerät, das
      // Kontakt hat, soll nicht mitten im Betrieb blind werden.
      expiresAt: stored?.expiresAt ?? new Date(Date.now() + 24 * 3_600_000).toISOString(),
      records,
      ...(this.scan ? { scan: this.scan } : {}),
    });
  }
}

/** Änderungen einspielen: gleiche id ersetzt, neue kommen dazu. */
export function mergeRecords(
  current: SnapshotRecord[],
  changed: SnapshotRecord[],
  removedIds: string[] = [],
): SnapshotRecord[] {
  if (changed.length === 0 && removedIds.length === 0) return current;
  const byId = new Map(current.map((r) => [r.id, r]));
  for (const record of changed) byId.set(record.id, record);
  // Grabsteine zuletzt: wer den Bereich verloren hat, muss vom Gerät
  // verschwinden, auch wenn er in derselben Runde noch als Änderung kam.
  for (const id of removedIds) byId.delete(id);
  return [...byId.values()];
}
